import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Progress, SignInPage, UserIdentity } from '@backstage/core-components';
import {
  oktaAuthApiRef,
  useAnalytics,
  useApi,
  type SignInPageProps,
} from '@backstage/core-plugin-api';
import { oktaProvider } from './signInProviders';

/**
 * Sign-in page for deployed environments: an unauthenticated visitor is sent
 * straight to Okta in the same tab, and lands back on the page they asked for
 * once Okta has authenticated them.
 *
 * The full-page redirect (rather than a popup window) comes from the
 * `enableExperimentalRedirectFlow: true` root config key set in
 * `app-config.k8s.yaml` — with it, `getBackstageIdentity({ instantPopup: true })`
 * navigates the tab to `/api/auth/okta/start?...&flow=redirect` instead of
 * calling `window.open`. Without that key this component still works, but falls
 * back to the popup the key exists to avoid.
 *
 * Upstream's `<SignInPage provider={oktaProvider} auto />` performs the same
 * redirect, but flips its internal `showLoginPage` state immediately before
 * navigating — which paints the "Sign in using Okta" card for a moment on the
 * way out. This component renders a progress indicator for the whole redirect
 * instead, and only falls back to that card when the automatic path fails.
 */
export const OktaRedirectSignInPage = (props: SignInPageProps) => {
  const { onSignInSuccess } = props;
  const authApi = useApi(oktaAuthApiRef);
  const analytics = useAnalytics();
  const [searchParams] = useSearchParams();

  // The auth backend appends `?error=` to the redirect target when the Okta
  // callback fails (nonce mismatch, sign-in resolver rejection, ...). Retrying
  // the redirect automatically would bounce the user between Backstage and
  // Okta forever, so hand over to the card, which renders that message and
  // offers a manual retry.
  const redirectFailed = Boolean(searchParams.get('error'));

  const [showCard, setShowCard] = useState(redirectFailed);
  const startedRef = useRef(false);

  const signIn = useCallback(async () => {
    try {
      // An existing session (the refresh-token cookie outlives a reload or a
      // return trip through Okta) means no redirect is needed.
      let identity = await authApi.getBackstageIdentity({ optional: true });

      if (!identity) {
        // Navigates this tab away, so this promise normally never settles.
        // Execution continues past it only if the redirect could not start.
        identity = await authApi.getBackstageIdentity({ instantPopup: true });
      }

      if (!identity) {
        setShowCard(true);
        return;
      }

      const profile = await authApi.getProfile();
      onSignInSuccess(
        UserIdentity.create({ identity: identity.identity, authApi, profile }),
      );
      analytics.captureEvent('signIn', 'success');
    } catch {
      // Surfaced by the card below, which reads the error from the auth API
      // itself when the user retries.
      setShowCard(true);
    }
  }, [analytics, authApi, onSignInSuccess]);

  useEffect(() => {
    if (redirectFailed || startedRef.current) {
      return;
    }
    startedRef.current = true;
    signIn();
  }, [redirectFailed, signIn]);

  if (showCard) {
    // No `auto`: the user clicks "Sign In" themselves, and the card shows the
    // `?error=` message when the redirect brought one back.
    return <SignInPage {...props} provider={oktaProvider} />;
  }

  return <Progress />;
};
